/**
 * 过程树 → cytoscape 元素转换工具。
 *
 * 主要职责：
 * 1. 递归遍历过程结构（请求体 process 字段），生成稳定唯一的节点 id；
 * 2. 给每个节点计算"分支路径编号"（1、2、2.1、2.3.1 等）；
 * 3. 给每个节点打上一个"主题色"，同一条根下面的子节点共用同一个颜色；
 * 4. 为每个叶子节点的 agents 生成角色槽位节点，挂在叶子节点右侧；
 * 5. 提供按 name/path 等维度查找节点 id 的辅助方法，以便流式事件驱动。
 */

/** 过程结构原始 JSON 的节点类型（与 configAndSendQuestion 中一致） */
export interface RawProcessNode {
  name: string;
  concurrency?: string;
  agents?: string[];
  instruments?: string[];
  constructions?: string[];
  effects?: string[];
  instrumentConditions?: Record<string, string>;
  processes?: RawProcessNode[];
}

/** 分支主题色 —— 参考设计图 3.png / 8.png：红、绿、橙、紫、蓝、粉 */
export const BRANCH_THEMES: { id: string; color: string; softBg: string }[] = [
  { id: 'red', color: '#ef4444', softBg: '#fee2e2' },
  { id: 'green', color: '#10b981', softBg: '#d1fae5' },
  { id: 'orange', color: '#f59e0b', softBg: '#fef3c7' },
  { id: 'purple', color: '#8b5cf6', softBg: '#ede9fe' },
  { id: 'blue', color: '#3b82f6', softBg: '#dbeafe' },
  { id: 'pink', color: '#ec4899', softBg: '#fce7f3' },
  { id: 'cyan', color: '#06b6d4', softBg: '#cffafe' },
];

/** 节点在 cytoscape 里的数据字段 */
export interface ProcessNodeData {
  id: string;
  /** 原始过程名（可能重复，但 id 一定唯一） */
  processName: string;
  /** 叶节点最终展示用的 label，已经拼接了编号 */
  label: string;
  /** 层级深度，0 表示根 */
  depth: number;
  /** 父过程节点 id，用于运行阶段根据正在执行的父链路消解同名节点 */
  parentProcessId?: string;
  /** 该节点所属的根分支编号（从 1 开始），用于挑选主题色 */
  branchIndex: number;
  /** 分支主题 id（red/green/...） */
  themeId: string;
  /** 分支主色 */
  themeColor: string;
  /** 分支柔和底色 */
  themeSoftBg: string;
  /** 完整的点分编号（如 "2.1"），根节点为空串 */
  numbering: string;
  /** 是否根节点 */
  isRoot: boolean;
  /** 是否叶子节点（无 processes） */
  isLeaf: boolean;
  /** 该叶子节点需要的角色数组，非叶子或无 agents 返回空数组 */
  agents: string[];
  /** 默认状态 */
  status: 'pending';
  /** 节点类型：process / role / result */
  nodeType: 'process';
  /** 根据 label 文字长度动态估算的节点宽度（px），用于 cytoscape style 的 width: data(nodeWidth) */
  nodeWidth: number;
  /** 动态估算的节点高度（px），短文本单行、长文本允许两行 */
  nodeHeight: number;
  layoutSeq: number;
}

/** 叶子节点右侧的角色槽位节点（数字员工匹配在这里展示） */
export interface RoleNodeData {
  id: string;
  /** 所属过程节点 id */
  parentProcessId: string;
  /** 原始角色名 */
  roleName: string;
  /** 展示标签（可能带"执行角色"字样或人员信息） */
  label: string;
  /** 槽位序号（同一过程节点内的顺序） */
  slotIndex: number;
  themeColor: string;
  themeSoftBg: string;
  /** 所属分支主题 id，用于套用分支配色 */
  themeId: string;
  nodeType: 'role';
  status: 'pending';
  nodeWidth: number;
  nodeHeight: number;
  layoutSeq: number;
}

/** 执行结果展示节点（第四步运行时挂在叶子节点上，初始化不创建） */
export interface ResultNodeData {
  id: string;
  parentProcessId: string;
  label: string;
  themeColor: string;
  themeSoftBg: string;
  themeId: string;
  nodeType: 'result';
  nodeWidth: number;
  nodeHeight: number;
  layoutSeq: number;
}

/**
 * 粗略估算一段标签在 cytoscape 画布内所需的宽度。
 *
 * - 中文字符按 14px 宽，英文/数字按 7.5px 宽（一般 font-size=12 时的保守估算）；
 * - 左右各留 padding；根节点、分组节点预留更大的内边距。
 */
export function estimateLabelWidth(
  label: string,
  opts: {
    fontSize?: number;
    paddingX?: number;
    min?: number;
    max?: number;
  } = {}
): number {
  const { fontSize = 12, paddingX = 24, min = 70, max = 260 } = opts;
  let px = 0;
  for (const ch of label) {
    // eslint-disable-next-line no-control-regex
    const isCJK = /[^\x00-\xff]/.test(ch);
    px += isCJK ? fontSize * 1.1 : fontSize * 0.62;
  }
  const width = Math.ceil(px + paddingX * 2);
  return Math.min(max, Math.max(min, width));
}

/** 过程树整体编译产物 */
export interface ProcessGraphCompileResult {
  /** 供 cytoscape 初始化用的 elements 数组 */
  elements: {
    group: 'nodes' | 'edges';
    data: Record<string, unknown>;
    classes?: string;
  }[];
  /** 运行阶段额外注入的结果节点元素（含 result-node 与对应 result-edge） */
  resultElements: {
    group: 'nodes' | 'edges';
    data: Record<string, unknown>;
    classes?: string;
  }[];
  /** 所有过程节点的 id 列表（按遍历顺序） */
  processIds: string[];
  /** name → 过程节点 id 列表（同名可能有多个） */
  nameToProcessIds: Map<string, string[]>;
  /** 过程节点 id → 数据 */
  processNodeMap: Map<string, ProcessNodeData>;
  /** 过程节点 id → 对应的角色槽位节点 id 数组（按 agents 顺序） */
  processToRoleIds: Map<string, string[]>;
  /** 角色槽位节点 id → 数据 */
  roleNodeMap: Map<string, RoleNodeData>;
  /** 过程节点 id → 结果节点 id（仅叶子节点有） */
  processToResultId: Map<string, string>;
  /** 结果节点 id → 数据 */
  resultNodeMap: Map<string, ResultNodeData>;
}

/**
 * 递归编译过程树到 cytoscape 元素集合。
 *
 * 规则：
 * - 根节点（第一层）不带编号，仅展示过程名。
 * - 直系子节点从 1 开始编号。
 * - 每个直系子节点分配一个分支主题色，所有后代共用该主题。
 * - 叶子节点的 agents 会一一转成独立的角色节点，通过"隐形"角色边挂在叶子右侧。
 */
export function compileProcessGraph(
  rootNode: RawProcessNode
): ProcessGraphCompileResult {
  const elements: ProcessGraphCompileResult['elements'] = [];
  const resultElements: ProcessGraphCompileResult['resultElements'] = [];
  const processIds: string[] = [];
  const nameToProcessIds = new Map<string, string[]>();
  const processNodeMap = new Map<string, ProcessNodeData>();
  const processToRoleIds = new Map<string, string[]>();
  const roleNodeMap = new Map<string, RoleNodeData>();
  const processToResultId = new Map<string, string>();
  const resultNodeMap = new Map<string, ResultNodeData>();

  let processCounter = 0;
  let roleCounter = 0;
  let resultCounter = 0;
  let layoutSeq = 0;

  /** 递归内部函数 */
  const traverse = (
    node: RawProcessNode,
    parentId: string | null,
    depth: number,
    numbering: string,
    branchIndex: number,
    theme: (typeof BRANCH_THEMES)[number]
  ) => {
    processCounter += 1;
    const nodeId = `p_${processCounter}`;
    const isRoot = depth === 0;
    const isLeaf = !node.processes || node.processes.length === 0;

    // 拼接最终 label：非根节点带点分编号
    const labelPrefix = isRoot ? '' : `${numbering} `;
    const label = `${labelPrefix}${node.name}`;

    // 根据节点类型预估宽度：根节点允许长一些，分组/叶子统一基础宽度
    const widthOpts = isRoot
      ? // 根节点标题字号更大，所以单独放宽宽度范围，避免首屏就被省略。
        { min: 340, max: 500, paddingX: 40, fontSize: 19 }
      : isLeaf
        ? { min: 240, max: 340, paddingX: 32, fontSize: 15 }
        : { min: 240, max: 360, paddingX: 32, fontSize: 15 };
    const nodeWidth = estimateLabelWidth(label, widthOpts);
    // 高度：椭圆造型下高度固定即可
    const nodeHeight = isRoot ? 96 : isLeaf ? 76 : 80;
    const processLayoutSeq = ++layoutSeq;

    const data: ProcessNodeData = {
      id: nodeId,
      processName: node.name,
      label,
      depth,
      parentProcessId: parentId || undefined,
      branchIndex,
      themeId: theme.id,
      themeColor: theme.color,
      themeSoftBg: theme.softBg,
      numbering,
      isRoot,
      isLeaf,
      agents: isLeaf ? node.agents || [] : [],
      status: 'pending',
      nodeType: 'process',
      nodeWidth,
      nodeHeight,
      layoutSeq: processLayoutSeq,
    };

    processIds.push(nodeId);
    processNodeMap.set(nodeId, data);
    const existing = nameToProcessIds.get(node.name) || [];
    existing.push(nodeId);
    nameToProcessIds.set(node.name, existing);

    // 初始 classes：status-pending 作为"未激活"标记；激活后 applyStatusClass 会
    // 切换为 `activated status-xxx`。注意 cytoscape 的选择器不支持 :not(.class) ，
    // 因此分支配色规则都绑定到 `.activated` 而非 `:not(.status-pending)`。
    elements.push({
      group: 'nodes',
      data: { ...data },
      classes: [
        'process-node',
        isRoot ? 'root' : isLeaf ? 'leaf' : 'group',
        `theme-${theme.id}`,
        'status-pending',
      ].join(' '),
    });

    if (parentId) {
      elements.push({
        group: 'edges',
        data: {
          id: `e_${parentId}__${nodeId}`,
          source: parentId,
          target: nodeId,
          edgeKind: 'process',
          themeColor: theme.color,
          layoutSeq: ++layoutSeq,
        },
        classes: `process-edge theme-${theme.id}${depth === 1 ? ' root-branch-edge' : ''}`,
      });
    }

    // 叶子节点追加角色槽位节点
    if (isLeaf && data.agents.length > 0) {
      const roleIds: string[] = [];
      data.agents.forEach((roleName, slotIndex) => {
        roleCounter += 1;
        const roleId = `r_${roleCounter}`;
        // 角色节点需要承载头像 + 姓名/角色两行信息，保持统一尺寸避免匹配后跳动。
        const roleWidth = 210;
        const roleData: RoleNodeData = {
          id: roleId,
          parentProcessId: nodeId,
          roleName,
          label: roleName,
          slotIndex,
          themeColor: theme.color,
          themeSoftBg: theme.softBg,
          themeId: theme.id,
          nodeType: 'role',
          status: 'pending',
          nodeWidth: roleWidth,
          nodeHeight: 64,
          layoutSeq: ++layoutSeq,
        };
        roleNodeMap.set(roleId, roleData);
        roleIds.push(roleId);

        elements.push({
          group: 'nodes',
          data: { ...roleData },
          classes: `role-node theme-${theme.id} status-pending`,
        });

        elements.push({
          group: 'edges',
          data: {
            id: `e_role_${nodeId}__${roleId}`,
            source: nodeId,
            target: roleId,
            edgeKind: 'role',
            themeColor: theme.color,
            lineLabel: '执行角色',
            layoutSeq: ++layoutSeq,
          },
          classes: `role-edge theme-${theme.id}`,
        });
      });
      processToRoleIds.set(nodeId, roleIds);
    }

    // 叶子节点预编译结果节点（第四步运行时再注入到图中）
    if (isLeaf) {
      resultCounter += 1;
      const resultId = `res_${resultCounter}`;
      const resultData: ResultNodeData = {
        id: resultId,
        parentProcessId: nodeId,
        label: '—',
        themeColor: theme.color,
        themeSoftBg: theme.softBg,
        themeId: theme.id,
        nodeType: 'result',
        // 第四步结果节点稍微放宽，给更大的文字预留横向展示空间。
        nodeWidth: 256,
        nodeHeight: 72,
        layoutSeq: ++layoutSeq,
      };
      processToResultId.set(nodeId, resultId);
      resultNodeMap.set(resultId, resultData);
      resultElements.push({
        group: 'nodes',
        data: { ...resultData },
        classes: `result-node theme-${theme.id}`,
      });
      resultElements.push({
        group: 'edges',
        data: {
          id: `e_result_${nodeId}__${resultId}`,
          source: nodeId,
          target: resultId,
          edgeKind: 'result',
          themeColor: theme.color,
          layoutSeq: ++layoutSeq,
        },
        classes: `result-edge theme-${theme.id}`,
      });
    }

    // 递归处理子节点
    if (!isLeaf) {
      node.processes!.forEach((child, childIdx) => {
        // 顶层第一层决定分支主题
        const nextBranchIndex = isRoot ? childIdx + 1 : branchIndex;
        const nextTheme = isRoot
          ? BRANCH_THEMES[childIdx % BRANCH_THEMES.length]
          : theme;
        // 根直系 → "1.","2."；更深层在父编号基础上剥去可能的尾点再拼子号，得到 "1.1","2.1.1" 等
        const parentBase = numbering.replace(/\.$/, '');
        const nextNumbering = isRoot
          ? `${childIdx + 1}.`
          : `${parentBase}.${childIdx + 1}`;
        traverse(
          child,
          nodeId,
          depth + 1,
          nextNumbering,
          nextBranchIndex,
          nextTheme
        );
      });
    }
  };

  // 根节点以黑色/灰色中性主题作为默认（被 isRoot 样式覆盖）
  traverse(rootNode, null, 0, '', 0, BRANCH_THEMES[0]);

  return {
    elements,
    resultElements,
    processIds,
    nameToProcessIds,
    processNodeMap,
    processToRoleIds,
    roleNodeMap,
    processToResultId,
    resultNodeMap,
  };
}

/**
 * 解析 "选中角色[场景故事编辑, 技术专家]" 或 "选中角色：[]" 的 content。
 * 返回角色名数组，无匹配返回 []。
 */
export function parseSelectedRoles(content: string): string[] {
  const match = content.match(/选中角色[:：]?\s*\[([^\]]*)\]/);
  if (!match) return [];
  return match[1]
    .split(/[,，]/)
    .map(s => s.trim())
    .filter(Boolean);
}
